import type { QueryClient } from '@tanstack/react-query';

import type {
  ContractSpaceLinePlan,
  PlannedItem,
  Space,
  WorkPackage
} from '@/api/generated/models';
import type { StatusTrackerStepData } from '@/components/common';
import { WorkPackageStatus } from '@/api/generated/models/workPackageStatus';
import {
  getListPlannedItemsByContractQueryKey,
  getListSpaceLinePlansByContractQueryKey,
  getListWorkPackagesByContractQueryKey
} from '@/api/generated/work-structure/work-structure';
import i18n from '@/i18n';

export interface SpaceOption {
  value: string;
  label: string;
}

/** Prostori idu prije djece (indentirano po dubini) — flat popis po objektu, stablo se gradi ovdje. */
export const buildSpaceOptions = (spaces: Space[]): SpaceOption[] => {
  const byParent = new Map<string, Space[]>();
  spaces.forEach((space) => {
    const key = space.parentSpaceId ?? '';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)?.push(space);
  });
  const options: SpaceOption[] = [];
  const walk = (parentKey: string, depth: number) => {
    (byParent.get(parentKey) ?? []).forEach((space) => {
      if (!space.id) return;
      options.push({
        value: space.id,
        label: `${depth > 0 ? `${'—'.repeat(depth)} ` : ''}${space.name ?? ''}`
      });
      walk(space.id, depth + 1);
    });
  };
  walk('', 0);
  return options;
};

/** Zadani prostor + svi njegovi podprostori (ista "grana" logika kao ContractSpaceBreakdown). */
export const collectDescendantSpaceIds = (spaces: Space[], rootSpaceId: string): Set<string> => {
  const childrenMap = new Map<string, string[]>();
  spaces.forEach((space) => {
    if (space.parentSpaceId && space.id) {
      childrenMap.set(space.parentSpaceId, [
        ...(childrenMap.get(space.parentSpaceId) ?? []),
        space.id
      ]);
    }
  });
  const collect = (id: string): string[] => {
    const direct = childrenMap.get(id) ?? [];
    return direct.flatMap((childId) => [childId, ...collect(childId)]);
  };
  return new Set([rootSpaceId, ...collect(rootSpaceId)]);
};

/** Stavke ugovora koje su preko razrade (ContractSpaceBreakdown, contract_space_line_plans) već
 * raspoređene negdje unutar zadane grane prostora — koristi se da izravno dodavanje stavki na
 * paketu (mimo razrade) ne nudi artikle koji nikad nisu dodijeljeni baš tom prostoru. */
export const linesPlannedInBranch = (
  spaceLinePlans: ContractSpaceLinePlan[],
  branchSpaceIds: Set<string>
): Set<string> => {
  const lineIds = new Set<string>();
  spaceLinePlans.forEach((plan) => {
    if (
      plan.spaceId &&
      branchSpaceIds.has(plan.spaceId) &&
      plan.contractLineId &&
      (plan.plannedQty ?? 0) > 0
    ) {
      lineIds.add(plan.contractLineId);
    }
  });
  return lineIds;
};

export interface SpaceLinePlanUsage {
  /** contract_space_line_plans.planned_qty za TOČNO ovaj prostor (ne granu) i ovu liniju. */
  ceiling: number;
  /** Zbroj planned_items.planned_qty preko SVIH radnih paketa u točno ovom prostoru za ovu liniju. */
  consumed: number;
}

/** Koliko je od DRAFT-planirane količine po (prostor, stavka) paru već stvarno potrošeno kroz
 * radne pakete TOČNO u ovom prostoru — mora pratiti backendov assertWithinSpacePlan (WorkStructureService),
 * koji gleda isključivo workPackage.spaceId, ne granu prostora kao linesPlannedInBranch iznad. */
export const computeSpaceLinePlanUsage = (
  spaceLinePlans: ContractSpaceLinePlan[],
  plannedItems: PlannedItem[],
  workPackages: WorkPackage[],
  spaceId: string
): Map<string, SpaceLinePlanUsage> => {
  const usageByLineId = new Map<string, SpaceLinePlanUsage>();
  spaceLinePlans.forEach((plan) => {
    if (plan.spaceId === spaceId && plan.contractLineId) {
      usageByLineId.set(plan.contractLineId, { ceiling: plan.plannedQty ?? 0, consumed: 0 });
    }
  });
  const workPackageSpaceId = new Map(workPackages.map((wp) => [wp.id, wp.spaceId]));
  plannedItems.forEach((item) => {
    if (!item.contractLineId || !item.workPackageId) return;
    if (workPackageSpaceId.get(item.workPackageId) !== spaceId) return;
    const usage = usageByLineId.get(item.contractLineId);
    if (usage) usage.consumed += item.plannedQty ?? 0;
  });
  return usageByLineId;
};

/** Osvježi sve iz čega se računa computeSpaceLinePlanUsage — razradu i stavke SVIH radnih paketa
 * ugovora. Zove se nakon svake promjene planiranih stavki i pri otvaranju pickera: inače drugi radni
 * paket istog prostora (ili drugi korisnik) ostavi keširanu, prenisku potrošnju, pa picker nudi
 * količinu koju backend (assertWithinSpacePlan) odbije s 409. */
export const invalidateContractSpaceUsage = (queryClient: QueryClient, contractId?: string) => {
  if (!contractId) return Promise.resolve();
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: getListPlannedItemsByContractQueryKey(contractId) }),
    queryClient.invalidateQueries({ queryKey: getListWorkPackagesByContractQueryKey(contractId) }),
    queryClient.invalidateQueries({ queryKey: getListSpaceLinePlansByContractQueryKey(contractId) })
  ]);
};

/** Puna putanja prostora (npr. "Bereich 4305V / Zone 4305V") hodajući parentSpaceId lancem. */
export const buildSpaceBreadcrumb = (spaceId: string | undefined, spaces: Space[]): string => {
  if (!spaceId) return '—';
  const byId = new Map(
    spaces.filter((space) => space.id).map((space) => [space.id as string, space])
  );
  const parts: string[] = [];
  let current = byId.get(spaceId);
  while (current) {
    parts.unshift(current.name ?? '');
    current = current.parentSpaceId ? byId.get(current.parentSpaceId) : undefined;
  }
  return parts.length ? parts.join(' / ') : '—';
};

export const statusConfig: Record<WorkPackageStatus, { labelKey: string; bg: string; fg: string }> =
  {
    [WorkPackageStatus.DRAFT]: {
      labelKey: 'workPackages.status.draft',
      bg: 'var(--color-bg-surface-2)',
      fg: 'var(--color-text-secondary)'
    },
    [WorkPackageStatus.ACTIVE]: {
      labelKey: 'workPackages.status.active',
      bg: 'var(--color-status-success-soft)',
      fg: 'var(--color-status-success)'
    },
    [WorkPackageStatus.COMPLETED]: {
      labelKey: 'workPackages.status.completed',
      bg: 'var(--color-status-info-soft)',
      fg: 'var(--color-status-info)'
    },
    [WorkPackageStatus.CANCELLED]: {
      labelKey: 'workPackages.status.cancelled',
      bg: 'var(--color-status-danger-soft)',
      fg: 'var(--color-status-danger)'
    }
  };

export const statusOptions = Object.values(WorkPackageStatus).map((status) => ({
  value: status,
  labelKey: statusConfig[status].labelKey
}));

const FLOW_ORDER = [WorkPackageStatus.DRAFT, WorkPackageStatus.ACTIVE, WorkPackageStatus.COMPLETED];

/** Koraci za StatusTracker (Figma node 4859:13767) — vidi offerDisplay.ts (buildOfferTrackerSteps)
 * za opću napomenu o principu. Radni paket nema partner/klijent razliku (jednotenantski entitet). */
export const buildWorkPackageTrackerSteps = (
  status: WorkPackageStatus
): StatusTrackerStepData[] => {
  const t = i18n.t.bind(i18n);
  const labelFor = (s: WorkPackageStatus) => t(statusConfig[s].labelKey);

  if (status === WorkPackageStatus.CANCELLED) {
    return [
      { key: 'draft', label: labelFor(WorkPackageStatus.DRAFT), state: 'done' },
      { key: 'exception', label: labelFor(WorkPackageStatus.CANCELLED), state: 'exception' }
    ];
  }

  const currentIndex = FLOW_ORDER.indexOf(status);
  return FLOW_ORDER.map((s, index) => ({
    key: s,
    label: labelFor(s),
    state: index < currentIndex ? 'done' : index > currentIndex ? 'upcoming' : 'current'
  }));
};
